import { useState, useEffect, useRef } from 'react';
import { X, Camera, Trash2, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { loadModels, captureEmbedding } from '../../lib/faceApi';

const PROMPTS = [
  'Olhe direto pra camera, expressao neutra',
  'Vire o rosto um pouco pra esquerda',
  'Vire o rosto um pouco pra direita',
  'Incline a cabeca levemente pra cima',
  'Olhe direto pra camera de novo',
];

const TOTAL_CAPTURAS = 5;

interface Rosto { id: number; criado_em: string }
interface Usuario { id: number; nome: string }

export function CadastrarRostoModal({ usuario, onFechar }: { usuario: Usuario; onFechar: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [modelosCarregados, setModelosCarregados] = useState(false);
  const [carregandoModelos, setCarregandoModelos] = useState(true);
  const [erroCamera, setErroCamera] = useState('');
  const [cameraStatus, setCameraStatus] = useState<'requesting' | 'ready' | 'error' | 'unsupported'>('requesting');
  const [rostosExistentes, setRostosExistentes] = useState<Rosto[]>([]);
  const [capturando, setCapturando] = useState(false);
  const [novasCapturas, setNovasCapturas] = useState(0);
  const [mensagem, setMensagem] = useState('');
  const [mensagemTipo, setMensagemTipo] = useState<'info' | 'success' | 'error'>('info');

  const concluido = novasCapturas >= TOTAL_CAPTURAS;

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        await loadModels();
        if (!cancelado) { setModelosCarregados(true); setCarregandoModelos(false); }
      } catch (err: any) {
        if (!cancelado) {
          setErroCamera('Falha ao carregar modelos de FR: ' + (err.message || err));
          setCarregandoModelos(false);
        }
      }
    })();
    return () => { cancelado = true; };
  }, []);

  useEffect(() => {
    let cancelado = false;
    async function iniciarCamera() {
      setCameraStatus('requesting');
      setErroCamera('');
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setCameraStatus('unsupported');
          setErroCamera('Navegador nao suporta camera.');
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 480, height: 360, facingMode: 'user' },
          audio: false,
        });
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        const track = stream.getVideoTracks()[0];
        if (!track || track.readyState !== 'live') {
          stream.getTracks().forEach((t) => t.stop());
          setCameraStatus('error');
          setErroCamera('Camera retornou track invalida. Outra aba/app esta usando — feche e tente de novo.');
          return;
        }
        track.addEventListener('ended', () => {
          setCameraStatus('error');
          setErroCamera('Camera foi desconectada. Feche outras abas e clique em "Tentar novamente".');
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch {}
        }
        setCameraStatus('ready');
      } catch (err: any) {
        setCameraStatus('error');
        const name = err?.name || '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setErroCamera('Permissao da camera negada. Cadeado da URL > Camera > Permitir > F5.');
        } else if (name === 'NotReadableError' || name === 'TrackStartError') {
          setErroCamera('Camera em uso por outra aba/app. Feche e tente de novo.');
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          setErroCamera('Nenhuma camera detectada neste computador.');
        } else if (name === 'OverconstrainedError') {
          setErroCamera('Camera nao suporta a resolucao pedida.');
        } else {
          setErroCamera('Erro de camera (' + name + '): ' + (err?.message || err));
        }
      }
    }
    void iniciarCamera();
    return () => {
      cancelado = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  useEffect(() => { void carregarRostos(); }, [usuario.id]);

  async function carregarRostos() {
    try {
      const data = await api.get<{ total: number; rostos: Rosto[] }>(`/api/funcionarios/${usuario.id}/rostos`);
      setRostosExistentes(data.rostos || []);
    } catch {
      setRostosExistentes([]);
    }
  }

  async function capturar() {
    if (!modelosCarregados || !videoRef.current || capturando) return;
    setCapturando(true);
    setMensagem('');
    try {
      const embedding = await captureEmbedding(videoRef.current);
      if (!embedding) {
        setMensagemTipo('error');
        setMensagem('Rosto nao detectado. Centralize-se na camera e tente de novo.');
        return;
      }
      await api.post(`/api/funcionarios/${usuario.id}/rostos`, { embedding });
      setNovasCapturas((n) => n + 1);
      setMensagemTipo('success');
      setMensagem(`Captura ${novasCapturas + 1}/${TOTAL_CAPTURAS} OK.`);
      void carregarRostos();
    } catch (err: any) {
      setMensagemTipo('error');
      setMensagem('Erro ao salvar: ' + err.message);
    } finally {
      setCapturando(false);
    }
  }

  async function limparTudo() {
    if (!confirm(`Apagar todos os ${rostosExistentes.length} rostos cadastrados de ${usuario.nome}?`)) return;
    try {
      await api.delete(`/api/funcionarios/${usuario.id}/rostos`);
      await carregarRostos();
      setNovasCapturas(0);
      setMensagemTipo('info');
      setMensagem('Rostos apagados. Pode recadastrar.');
    } catch (err: any) {
      setMensagemTipo('error');
      setMensagem('Erro ao apagar: ' + err.message);
    }
  }

  async function removerRosto(id: number) {
    try {
      await api.delete(`/api/funcionarios/rostos/${id}`);
      void carregarRostos();
    } catch (err: any) {
      alert(err.message || 'Erro ao remover');
    }
  }

  return (
    // z-[1100]: desde a DA-456 este modal abre de DENTRO do modal de editar usuario, que e um
    // <Modal/> padrao em z-index 1000. Em z-[60] ele renderizava ATRAS e sumia da tela.
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4">
      <div className="rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--panel-bg)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 z-10" style={{ backgroundColor: 'var(--panel-bg)', borderColor: 'var(--border)' }}>
          <h3 className="font-semibold">
            Cadastrar Rosto — <span className="text-blue-700">{usuario.nome}</span>
          </h3>
          <button onClick={onFechar} className="p-1 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 grid md:grid-cols-2 gap-5">
          <div>
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              {(carregandoModelos || erroCamera || cameraStatus === 'requesting') && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/75 text-white text-sm text-center px-4">
                  {erroCamera ? (
                    <div className="flex flex-col items-center gap-2">
                      <AlertCircle size={20} className="text-orange-400" />
                      <div>{erroCamera}</div>
                    </div>
                  ) : carregandoModelos ? (
                    <div className="flex items-center gap-2">
                      <Loader2 size={18} className="animate-spin" />
                      Carregando modelos de FR...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Loader2 size={18} className="animate-spin" />
                      Aguardando permissao da camera...
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-3 text-center">
              {!concluido && (
                <p className="text-sm mb-2 min-h-[20px]" style={{ color: 'var(--text-muted)' }}>
                  {PROMPTS[novasCapturas] || ''}
                </p>
              )}
              <button
                onClick={() => void capturar()}
                disabled={!modelosCarregados || !!erroCamera || capturando || concluido}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {capturando ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                {concluido ? 'Concluido' : `Capturar ${novasCapturas + 1}/${TOTAL_CAPTURAS}`}
              </button>
              {mensagem && (
                <div className={`mt-3 text-sm px-3 py-2 rounded-lg ${
                  mensagemTipo === 'success' ? 'bg-green-50 text-green-700' :
                  mensagemTipo === 'error' ? 'bg-red-50 text-red-700' :
                  'bg-blue-50 text-blue-700'
                }`}>
                  {mensagem}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-sm">Progresso desta sessao</h4>
            </div>
            <div className="grid grid-cols-5 gap-2 mb-4">
              {Array.from({ length: TOTAL_CAPTURAS }).map((_, i) => (
                <div
                  key={i}
                  className={`aspect-square rounded-lg border-2 flex items-center justify-center text-xs font-bold ${
                    i < novasCapturas
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-gray-50 text-gray-300'
                  }`}
                >
                  {i < novasCapturas ? <CheckCircle2 size={20} /> : i + 1}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mb-2 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
              <h4 className="font-semibold text-sm">Rostos cadastrados ({rostosExistentes.length})</h4>
              {rostosExistentes.length > 0 && (
                <button
                  onClick={() => void limparTudo()}
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                >
                  <Trash2 size={13} /> Limpar todos
                </button>
              )}
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {rostosExistentes.length === 0 ? (
                <div className="text-xs italic" style={{ color: 'var(--text-muted)' }}>Nenhum rosto cadastrado ainda.</div>
              ) : (
                rostosExistentes.map((r, i) => (
                  <div key={r.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded" style={{ backgroundColor: 'var(--card-bg)' }}>
                    <span style={{ color: 'var(--text-muted)' }}>
                      #{i + 1} — {new Date(r.criado_em).toLocaleString('pt-BR')}
                    </span>
                    <button onClick={() => void removerRosto(r.id)} className="text-red-500 hover:text-red-700" title="Remover">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>

            {concluido && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 size={16} /> Cadastro concluido
                </div>
                <p className="text-xs mt-1">
                  {TOTAL_CAPTURAS} rostos capturados. Ative o "Controle de Acesso" no usuario pra exigir ponto.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-end" style={{ borderColor: 'var(--border)' }}>
          <button onClick={onFechar} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Fechar</button>
        </div>
      </div>
    </div>
  );
}
